import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  QUESTION_TYPES,
  QUESTION_TYPES_WITH_SUBQUESTIONS,
  translateBulk,
} from '@api/instant_survey';
import { stripHtml, toast } from '@utilities';

import { isLanguageScreenerQuestion } from '../../../../../utilities/helpers';
import { getTranslationModalMessage } from './getTranslationModalMessage';

// Lazily translates visible question sections instead of one large upfront request.

const MAX_CONCURRENT_QUESTIONS = 2;
const OBSERVER_MARGIN = '0px';

const isAbortError = (error) => error?.name === 'AbortError' || error?.name === 'CanceledError';

const isMaxDiffQuestion = (question) => question.type === QUESTION_TYPES.MAX_DIFF.value;

// Use the element that actually scrolls as the IntersectionObserver root.
const getScrollParent = (node) => {
  if (typeof window === 'undefined') return null;
  let element = node?.parentElement;
  while (element) {
    const { overflowY } = window.getComputedStyle(element);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      element.scrollHeight > element.clientHeight
    ) {
      return element;
    }
    element = element.parentElement;
  }
  return null;
};

const getRootBounds = (root) => {
  if (root) return root.getBoundingClientRect();
  return { bottom: window.innerHeight, top: 0 };
};

const isWithinBounds = (bounds, rootBounds, margin = 0) =>
  bounds.bottom >= rootBounds.top - margin && bounds.top <= rootBounds.bottom + margin;

const hasTranslationText = (richText) =>
  Boolean(
    stripHtml(richText || '')
      .replace(/\u00a0/g, ' ')
      .trim()
  );

/**
 * Scroll-driven translation for visible question sections.
 *
 * @param {Object} options
 * @param {String[]} options.appliedLanguages target languages shown side by side
 * @param {(results: {language: String, translatedItems: Object}[]) => void} options.applyTranslations
 *   merges a question's results into the modal's local store (with preserveUserEdits)
 * @param {React.RefObject<HTMLElement>} options.scrollRootRef element inside the modal scroll area
 * @param {String} options.sourceLanguage source language code
 * @param {Object} options.survey the survey (source text for questions/options)
 * @param {{ current: Object }} options.translationsRef latest translations map
 * @return {{ isTranslating: boolean, prioritizeQuestion: (questionId) => void,
 *   registerQuestion: (questionId) => (node: HTMLElement | null) => void }}
 */
export const useOnScreenTranslation = ({
  appliedLanguages,
  applyTranslations,
  scrollRootRef,
  sourceLanguage,
  survey,
  translationsRef,
}) => {
  const questions = survey.questions || [];
  // Primitive key so resets run only on real target-language changes.
  const appliedLanguagesKey = appliedLanguages.join('|');

  const sourceByQuestionId = useMemo(() => {
    const map = new Map();
    questions.forEach((question) => {
      // Skip content pending deletion.
      if (question._delete) return;
      const entry = { options: {}, properties: {}, subQuestions: {} };
      if (isLanguageScreenerQuestion(question)) {
        map.set(String(question.id), entry);
        return;
      }
      const questionSource = question.richText || question.text;
      if (questionSource) entry.text = questionSource;
      if (isMaxDiffQuestion(question)) {
        const { bestLabel, worstLabel } = question.properties || {};
        if (bestLabel) entry.properties.bestLabel = bestLabel;
        if (worstLabel) entry.properties.worstLabel = worstLabel;
      }
      (question.answers || []).forEach((answer) => {
        if (answer._delete) return;
        const optionSource = answer.richText || answer.text;
        if (optionSource) entry.options[answer.id] = optionSource;
      });
      // Grid rows are translated alongside their question text and options.
      if (QUESTION_TYPES_WITH_SUBQUESTIONS.includes(question.type)) {
        (question.subQuestions || []).forEach((subQuestion) => {
          if (subQuestion._delete) return;
          const subQuestionSource = subQuestion.richText || subQuestion.text;
          if (subQuestionSource) entry.subQuestions[subQuestion.id] = subQuestionSource;
        });
      }
      map.set(String(question.id), entry);
    });
    return map;
  }, [survey]);

  const [isTranslating, setIsTranslating] = useState(false);

  const queueRef = useRef([]);
  const inFlightRef = useRef(new Set());
  const activeRef = useRef(0);
  // Prevent aborted requests from changing queue state after reset/unmount.
  const generationRef = useRef(0);
  const controllerRef = useRef(null);
  const erroredRef = useRef(false);

  // Latest values for async/observer callbacks.
  const appliedLanguagesRef = useRef(appliedLanguages);
  appliedLanguagesRef.current = appliedLanguages;
  const sourceLanguageRef = useRef(sourceLanguage);
  sourceLanguageRef.current = sourceLanguage;
  const applyTranslationsRef = useRef(applyTranslations);
  applyTranslationsRef.current = applyTranslations;

  // A string is fetched when it is missing, blank, or is a stale machine translation. Stale
  // user-edited strings keep their text and show the warning until dismissed.
  const needsFetch = useCallback(
    (entityKey, language) => {
      const entry = translationsRef.current[`${entityKey}:${language}`];
      if (!entry || !hasTranslationText(entry.richText)) return true;
      return entry.isRelevant === false && entry.isUserUpdated !== true;
    },
    [translationsRef]
  );

  const questionNeedsFetch = useCallback(
    (questionId) => {
      const source = sourceByQuestionId.get(questionId);
      if (!source) return false;
      return appliedLanguagesRef.current.some((language) => {
        if (source.text && needsFetch(`question-${questionId}`, language)) return true;
        if (
          Object.keys(source.options).some((optionId) =>
            needsFetch(`option-${questionId}-${optionId}`, language)
          )
        ) {
          return true;
        }
        if (
          source.properties?.bestLabel &&
          needsFetch(`maxdiff-best-label-${questionId}`, language)
        ) {
          return true;
        }
        if (
          source.properties?.worstLabel &&
          needsFetch(`maxdiff-worst-label-${questionId}`, language)
        ) {
          return true;
        }
        return Object.keys(source.subQuestions || {}).some((subQuestionId) =>
          needsFetch(`subquestion-${questionId}-${subQuestionId}`, language)
        );
      });
    },
    [needsFetch, sourceByQuestionId]
  );

  // Fetch still-needed strings for one question across applied languages.
  const fetchQuestion = useCallback(
    async (questionId, signal) => {
      const source = sourceByQuestionId.get(questionId);
      if (!source) return;

      const perLanguage = appliedLanguagesRef.current
        .map((language) => {
          const questionEntry = {};
          if (source.text && needsFetch(`question-${questionId}`, language)) {
            questionEntry.text = source.text;
          }
          const options = {};
          Object.entries(source.options).forEach(([optionId, optionSource]) => {
            if (needsFetch(`option-${questionId}-${optionId}`, language)) {
              options[optionId] = optionSource;
            }
          });
          if (Object.keys(options).length) questionEntry.options = options;
          const properties = {};
          if (
            source.properties?.bestLabel &&
            needsFetch(`maxdiff-best-label-${questionId}`, language)
          ) {
            properties.bestLabel = source.properties.bestLabel;
          }
          if (
            source.properties?.worstLabel &&
            needsFetch(`maxdiff-worst-label-${questionId}`, language)
          ) {
            properties.worstLabel = source.properties.worstLabel;
          }
          if (Object.keys(properties).length) questionEntry.properties = properties;
          const subQuestions = {};
          Object.entries(source.subQuestions || {}).forEach(
            ([subQuestionId, subQuestionSource]) => {
              if (needsFetch(`subquestion-${questionId}-${subQuestionId}`, language)) {
                subQuestions[subQuestionId] = subQuestionSource;
              }
            }
          );
          if (Object.keys(subQuestions).length) questionEntry.subQuestions = subQuestions;
          return {
            items: {
              questions: Object.keys(questionEntry).length ? { [questionId]: questionEntry } : {},
            },
            language,
          };
        })
        .filter(({ items }) => Object.keys(items.questions).length > 0);
      if (!perLanguage.length) return;

      const settled = await Promise.allSettled(
        perLanguage.map(({ items, language }) =>
          translateBulk({
            fromLang: sourceLanguageRef.current,
            isRichText: true,
            items,
            signal,
            toLang: language,
          }).then((translatedItems) => ({ language, translatedItems }))
        )
      );

      const results = settled
        .filter((outcome) => outcome.status === 'fulfilled')
        .map((outcome) => outcome.value);
      if (results.length) applyTranslationsRef.current(results);

      const hadRealError = settled.some(
        (outcome) => outcome.status === 'rejected' && !isAbortError(outcome.reason)
      );
      if (hadRealError && !erroredRef.current) {
        erroredRef.current = true;
        toast.error(getTranslationModalMessage('fetchError', sourceLanguageRef.current));
      }
    },
    [needsFetch, sourceByQuestionId]
  );

  // Stored in a ref so async callbacks always call the latest drain.
  const drainRef = useRef(null);
  const drain = useCallback(() => {
    while (activeRef.current < MAX_CONCURRENT_QUESTIONS && queueRef.current.length) {
      const questionId = queueRef.current.shift();
      if (inFlightRef.current.has(questionId)) continue;
      if (!questionNeedsFetch(questionId)) continue;

      inFlightRef.current.add(questionId);
      activeRef.current += 1;
      setIsTranslating(true);
      const generation = generationRef.current;
      const signal = controllerRef.current?.signal;

      fetchQuestion(questionId, signal)
        .catch(() => {})
        .finally(() => {
          inFlightRef.current.delete(questionId);
          if (generation !== generationRef.current) return;
          activeRef.current -= 1;
          drainRef.current?.();
          if (activeRef.current === 0 && !queueRef.current.length) setIsTranslating(false);
        });
    }
    if (activeRef.current === 0 && !queueRef.current.length) setIsTranslating(false);
  }, [fetchQuestion, questionNeedsFetch]);
  drainRef.current = drain;

  const enqueueQuestion = useCallback((questionId) => {
    const id = String(questionId);
    if (inFlightRef.current.has(id)) return;
    if (!queueRef.current.includes(id)) queueRef.current.push(id);
    drainRef.current?.();
  }, []);
  const enqueueRef = useRef(enqueueQuestion);
  enqueueRef.current = enqueueQuestion;

  const observerRef = useRef(null);
  const nodesRef = useRef(new Map());
  const questionIdByNodeRef = useRef(new Map());
  const refCallbacksRef = useRef(new Map());

  const queueQuestionsInViewport = useCallback(() => {
    const root = getScrollParent(scrollRootRef.current);
    const rootBounds = getRootBounds(root);
    nodesRef.current.forEach((node, questionId) => {
      const bounds = node.getBoundingClientRect();
      if (isWithinBounds(bounds, rootBounds)) {
        enqueueRef.current(questionId);
      }
    });
  }, [scrollRootRef]);

  // Reset in-flight work only when applied/source languages change.
  useEffect(() => {
    generationRef.current += 1;
    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    inFlightRef.current.clear();
    activeRef.current = 0;
    queueRef.current = [];
    erroredRef.current = false;

    if (appliedLanguages.length) {
      queueQuestionsInViewport();
    } else {
      setIsTranslating(false);
    }

    return () => {
      generationRef.current += 1;
      controllerRef.current?.abort();
    };
  }, [appliedLanguages.length, appliedLanguagesKey, queueQuestionsInViewport, sourceLanguage]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const root = getScrollParent(scrollRootRef.current);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const questionId = questionIdByNodeRef.current.get(entry.target);
          if (!questionId) return;
          if (entry.isIntersecting) {
            enqueueRef.current(questionId);
          }
        });
      },
      { root, rootMargin: OBSERVER_MARGIN, threshold: 0 }
    );
    observerRef.current = observer;
    nodesRef.current.forEach((node) => observer.observe(node));

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [appliedLanguagesKey, scrollRootRef]);

  // Fallback for modal layouts where IntersectionObserver misses shell scrolling.
  useEffect(() => {
    if (!appliedLanguages.length || typeof window === 'undefined') return undefined;

    const root = getScrollParent(scrollRootRef.current);
    const modal = scrollRootRef.current?.closest?.('.modal');
    const scrollTargets = Array.from(new Set([root, modal, window].filter(Boolean)));
    let frame = null;

    const handleScroll = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        queueQuestionsInViewport();
      });
    };

    scrollTargets.forEach((target) => {
      target.addEventListener('scroll', handleScroll, { passive: true });
    });
    handleScroll();

    return () => {
      scrollTargets.forEach((target) => {
        target.removeEventListener('scroll', handleScroll);
      });
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [
    appliedLanguages.length,
    appliedLanguagesKey,
    queueQuestionsInViewport,
    scrollRootRef,
    sourceLanguage,
  ]);

  const registerQuestion = useCallback((questionId) => {
    const id = String(questionId);
    if (!refCallbacksRef.current.has(id)) {
      refCallbacksRef.current.set(id, (node) => {
        const previousNode = nodesRef.current.get(id);
        if (previousNode && previousNode !== node) {
          observerRef.current?.unobserve(previousNode);
          questionIdByNodeRef.current.delete(previousNode);
        }
        if (node) {
          nodesRef.current.set(id, node);
          questionIdByNodeRef.current.set(node, id);
          observerRef.current?.observe(node);
        } else if (previousNode) {
          nodesRef.current.delete(id);
        }
      });
    }
    return refCallbacksRef.current.get(id);
  }, []);

  return { isTranslating, prioritizeQuestion: enqueueQuestion, registerQuestion };
};
